import { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { getPurchaseOrderExtractionDraft } from '../../clients/HrpsExtractionsApi';
import { DUPLICATE_PURCHASE_ORDER_TYPE } from '../../constants';
import { PO_NUMBER_INPUT_PATTERN } from '../../constants/customerReference';
import { isOrderableQuantity } from '../../constants/orderQuantity';
import { ExtractedLineItem, GetPurchaseOrderExtractionDraftOutput } from '../../types';
import { todayIsoDate } from '../../utils/dateUtils';
import { Logger } from '../../utils/logger';
import { PoReviewDraft, PoReviewRow } from '../types';
import {
    PO_REVIEW_DRAFT_CLEAR,
    PO_REVIEW_DRAFT_GET,
    PO_REVIEW_DRAFT_LINE_ADDED,
    PO_REVIEW_DRAFT_LINE_REMOVED,
    PO_REVIEW_DRAFT_LOADED,
    PO_REVIEW_DRAFT_PO_NUMBER_CHANGED,
    PO_REVIEW_DRAFT_ROW_ITEM_CHANGED,
    PO_REVIEW_DRAFT_ROW_QUANTITY_CHANGED,
    PO_REVIEW_DRAFT_SHIP_DATE_CHANGED,
    PO_REVIEW_DRAFT_SHIPPING_LOCATION_CODE_CHANGED,
    PoReviewDraftActions,
} from './poReviewDraftActions';
import { getUnmappedItemsClear } from './resellerActionCreators';

export function poReviewDraftLoading(): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_GET,
        payload: {
            status: 'Loading',
            error: undefined,
            value: undefined,
        },
    };
}

export function poReviewDraftLoaded(draft: PoReviewDraft): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_LOADED,
        payload: {
            status: 'Loaded',
            error: undefined,
            value: draft,
        },
    };
}

export function poReviewDraftFailed(error: any): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_LOADED,
        payload: {
            status: 'Failed',
            error,
            value: undefined,
        },
    };
}

export function loadPoReviewDraft(resellerId: string, extractionId: string) {
    return (dispatch: Dispatch) => {
        dispatch(getUnmappedItemsClear());
        dispatch(poReviewDraftLoading());
        return getPurchaseOrderExtractionDraft({ resellerId, extractionId })
            .then((response) => {
                dispatch(poReviewDraftLoaded(toPoReviewDraft(response)));
            })
            .catch((error) => {
                Logger.debug(`error getting purchase order extraction draft: ${error}`);
                dispatch(poReviewDraftFailed(error));
            });
    };
}

export function setPoNumber(poNumber: string): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_PO_NUMBER_CHANGED,
        payload: poNumber,
    };
}

export function setShipDate(shipDate: string): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_SHIP_DATE_CHANGED,
        payload: shipDate,
    };
}

export function setShippingLocationCode(shippingLocationCode: string): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_SHIPPING_LOCATION_CODE_CHANGED,
        payload: shippingLocationCode,
    };
}

export function setRowItem(rowId: string, itemId?: string): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_ROW_ITEM_CHANGED,
        payload: { rowId, itemId },
    };
}

export function setRowQuantity(rowId: string, quantity: string): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_ROW_QUANTITY_CHANGED,
        payload: { rowId, quantity },
    };
}

export function addLine(): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_LINE_ADDED,
        payload: { rowId: uuidv4() },
    };
}

export function removeLine(rowId: string): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_LINE_REMOVED,
        payload: { rowId },
    };
}

export function clearPoReviewDraft(): PoReviewDraftActions {
    return {
        type: PO_REVIEW_DRAFT_CLEAR,
    };
}

// Turns the optional API response shape into the shape the page can rely on. A ship date it could not read
// leaves the field empty and keeps the printed text, so the empty field can still explain itself.
function toPoReviewDraft(response: GetPurchaseOrderExtractionDraftOutput): PoReviewDraft {
    const shipDateNormalizationFailed = response.shipDate?.normalizationFailed === true;
    const readShipDate = shipDateNormalizationFailed ? '' : response.shipDate?.value || '';
    const shipDateInPast = readShipDate !== '' && readShipDate < todayIsoDate();
    const shipDateUnusable = shipDateNormalizationFailed || shipDateInPast;
    const shipDate = shipDateInPast ? '' : readShipDate;
    const poNumberValue = response.poNumber?.value || '';
    const poNumberNotRead =
        response.poNumber?.normalizationFailed === true ||
        !PO_NUMBER_INPUT_PATTERN.test(poNumberValue) ||
        poNumberValue === '';
    const poNumber = poNumberNotRead ? '' : poNumberValue;
    const documentFindings = response.documentFindings || [];
    const alreadyOrdered = documentFindings.some((finding) => finding.type === DUPLICATE_PURCHASE_ORDER_TYPE);
    return {
        extractionId: response.extractionId,
        status: response.status,
        createdAt: response.createdAt,
        source: response.source,
        fileName: response.fileName,
        sourceDocument: response.sourceDocument,
        plaintextAvailable: response.plaintextAvailable,
        shipToAddress: response.shipToAddress,
        documentFindings,
        poNumber,
        alreadyOrderedPoNumber: alreadyOrdered ? poNumber : undefined,
        poNumberNotRead,
        poNumberRawValue: poNumberNotRead ? response.poNumber?.value : undefined,
        poNumberExtractedValue: poNumberNotRead ? undefined : poNumberValue || undefined,
        shipDate,
        shipDateNotRead: shipDate === '',
        shipDateRawValue: shipDateUnusable ? response.shipDate?.value : undefined,
        shipDateInPast,
        shippingLocationCode: response.shipToAddress?.matchedLocationCode,
        rows: (response.lineItems || []).map(toPoReviewRow),
    };
}

function toPoReviewRow(lineItem: ExtractedLineItem, index: number): PoReviewRow {
    const quantity = lineItem.quantity || {};
    const quantityValue = quantity.value || '';
    const quantityUnusable =
        quantity.normalizationFailed === true || (quantityValue !== '' && !isOrderableQuantity(quantityValue));
    return {
        rowId: `row-${index}`,
        itemNumber: index + 1,
        isAdded: false,
        itemId: lineItem.itemId ?? undefined,
        itemMatch: lineItem.itemMatch ?? undefined,
        codes: lineItem.codes,
        quantity: quantityUnusable ? '' : quantityValue,
        quantityNotRead: quantityUnusable,
        quantityRawValue: quantityUnusable ? quantity.value : undefined,
        findings: lineItem.findings || [],
    };
}
